'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from './utils';
import { useInertBackground } from './useInertBackground';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

function SheetContent({
  className,
  children,
  side = 'right',
  overlayClassName,
  title = 'Sheet',
  description,
  showClose = true,
  ...props
}: Omit<React.ComponentProps<typeof SheetPrimitive.Content>, 'title'> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  overlayClassName?: string;
  /** Accessible title for screen readers. Rendered visually hidden when provided; use visible SheetTitle in children for a visible heading. Pass null to suppress when a visible SheetTitle is provided in children. */
  title?: string | null;
  /**
   * Renders the built-in top-right `SheetPrimitive.Close` button. Set to
   * `false` when the caller mounts its own close control in `children` —
   * otherwise both remain in the tab order and Tab-wrap lands on the
   * unlabeled built-in button instead of the caller's own close (see
   * MobileFilterDrawer, WEB-792).
   */
  showClose?: boolean;
  /**
   * Accessible description for screen readers. Rendered visually hidden as a
   * `<SheetPrimitive.Description>` and wired through `aria-describedby` so the
   * Radix Dialog warning ("Missing `Description` or `aria-describedby={undefined}`
   * for {DialogContent}") stays quiet. Pass `null` (or omit) only when a
   * visible `SheetDescription` is already mounted as a child — in that case
   * the caller is responsible for the `aria-describedby` plumbing.
   *
   * @see WEB-274 — Discover a11y sweep (Map results panel description).
   */
  description?: string | null;
}) {
  // Radix dispatches a console warning whenever `aria-describedby` is set to
  // `undefined` and no `<Dialog.Description>` is mounted. We have two contracts:
  //   1. caller provides `description` — render a visually-hidden description
  //      and wire `aria-describedby` to its id; or
  //   2. caller omits/null — fall back to `aria-describedby={undefined}` so
  //      the existing call sites (which intentionally have no description)
  //      keep their current behaviour. This branch still emits the Radix
  //      warning in dev, by design — adding a description is the fix.
  const reactId = React.useId();
  const descriptionId = description != null ? `sheet-description-${reactId}` : undefined;

  // WEB-291: SheetContent is only mounted (via SheetPortal) while the sheet is
  // open/animating, so this is scoped to the open lifetime. It converts the
  // background `aria-hidden` Radix stamps into `inert`, which keeps the
  // background out of the tab order and a11y tree without the
  // "Blocked aria-hidden ... descendant retained focus" console warning.
  useInertBackground(true);

  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        aria-describedby={descriptionId}
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className,
        )}
        {...props}
      >
        {title != null && <SheetPrimitive.Title className="sr-only">{title}</SheetPrimitive.Title>}
        {description != null && (
          <SheetPrimitive.Description id={descriptionId} className="sr-only">
            {description}
          </SheetPrimitive.Description>
        )}
        {children}
        {showClose && (
          <SheetPrimitive.Close
            // `size-11` + the `min-h/min-w` floor is the mobile/tablet touch
            // target; `desktop:` (>=1024px) shrinks to the DS compact icon-button
            // size and clears the now-redundant floor.
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-3 right-3 inline-flex size-11 min-h-[44px] min-w-[44px] desktop:size-9 desktop:min-h-0 desktop:min-w-0 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
          >
            <XIcon className="size-4 shrink-0" aria-hidden />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-1.5 p-4', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
